import { AxiosRequestConfig } from 'axios'
import createAPIClient from '../services/apiClient'
import {
  Aurora,
  AuroraAwhpData,
  AuroraAwhpDataQuery,
  AuroraAwhpEvent,
  AuroraAwhpEventQuery,
  AuroraAwhpResponse,
  AuroraInput
} from '../types'
import { subMinutes } from 'date-fns'
import AuroraAwhpActionInput from '../types/AuroraAwhpActionInput'
import {
  AuroraBoilerRunPermitStatus,
  AuroraBoilerMainPowerStatus,
  AuroraCirculationPumpMode,
  AuroraCirculationPumpStatus,
  AuroraHeatPumpMode,
  AuroraHeatPumpStatus,
  AuroraHeatingElementMode,
  AuroraHeatingElementStatus
} from '../enums'
import { ulid } from 'ulid'
import { AuroraAwhpActionNack } from '../errors'

const api = createAPIClient('v2')

export async function getAuroras(config?: AxiosRequestConfig) {
  const response = await api.get<Aurora[]>('/auroras', config)

  return response.data
}

export async function getAurora(id: string, config?: AxiosRequestConfig) {
  const response = await api.get<Aurora>(`/auroras/${id}`, config)

  return response.data
}

export async function updateAurora(id: string, input: AuroraInput, config?: AxiosRequestConfig) {
  const response = await api.put<Aurora>(`/auroras/${id}`, input, config)

  return response.data
}

export async function getAuroraAwhpData(
  query: AuroraAwhpDataQuery,
  config?: Omit<AxiosRequestConfig, 'params'>
) {
  const response = await api.get<AuroraAwhpData[]>(`/auroras/awhp-data`, {
    params: query,
    ...config
  })

  return response.data
}

export async function getAuroraAwhpDataNow(
  id: string,
  config?: Omit<AxiosRequestConfig, 'params'>
) {
  const response = await api.get<AuroraAwhpData[]>(`/auroras/${id}/awhp-data`, {
    ...config,
    params: {
      aurora_id: id,
      from: subMinutes(new Date(), 3),
      to: new Date()
    }
  })

  if (response.data.length === 0) {
    throw new Error(`No AWHP data found for Aurora ${id}`)
  }

  return response.data[0]
}

export async function getAuroraAwhpEvents(
  query: AuroraAwhpEventQuery,
  config?: Omit<AxiosRequestConfig, 'params'>
) {
  const response = await api.get<AuroraAwhpEvent[]>(`/auroras/awhp-events`, {
    params: query,
    ...config
  })

  return response.data
}

export async function publishAuroraAwhpAction(
  id: string,
  action: AuroraAwhpActionInput,
  config?: AxiosRequestConfig
) {
  const correlationId = ulid()

  await api.post(
    `/auroras/${id}/awhp/action`,
    {
      ...action,
      correlationId
    },
    config
  )

  // Wait for one second before polling for status.
  const ONE_SECOND = 1000
  await wait(ONE_SECOND)

  const status = await pollAuroraAwhpResponse(id, correlationId, config)

  if (status?.action_nack) {
    throw new AuroraAwhpActionNack(status.action_nack)
  }
}

export async function setAuroraHeatPumpStatus(
  id: string,
  value: AuroraHeatPumpStatus,
  config?: AxiosRequestConfig
) {
  await publishAuroraAwhpAction(
    id,
    {
      hp: {
        system_on: value
      }
    },
    config
  )
}

export async function setAuroraHeatPumpMode(
  id: string,
  value: AuroraHeatPumpMode,
  config?: AxiosRequestConfig
) {
  await publishAuroraAwhpAction(
    id,
    {
      hp: {
        operation_mode: value
      }
    },
    config
  )
}

export async function setAuroraTargetTemperature(
  id: string,
  value: number,
  config?: AxiosRequestConfig
) {
  await publishAuroraAwhpAction(
    id,
    {
      hp: {
        setTarget1: value
      }
    },
    config
  )
}

export async function setAuroraCirculationPumpStatus(
  id: string,
  value: AuroraCirculationPumpStatus,
  config?: AxiosRequestConfig
) {
  await publishAuroraAwhpAction(
    id,
    {
      pump: {
        run: value
      }
    },
    config
  )
}

export async function setAuroraCirculationPumpMode(
  id: string,
  mode: AuroraCirculationPumpMode,
  config?: AxiosRequestConfig
) {
  await publishAuroraAwhpAction(
    id,
    {
      pump: {
        mode
      }
    },
    config
  )
}

export async function setAuroraBoilerRunPermitStatus(
  id: string,
  value: AuroraBoilerRunPermitStatus,
  config?: AxiosRequestConfig
) {
  await publishAuroraAwhpAction(
    id,
    {
      boiler: {
        run_permit: value
      }
    },
    config
  )
}

export async function setAuroraBoilerMainPowerStatus(
  id: string,
  value: AuroraBoilerMainPowerStatus,
  config?: AxiosRequestConfig
) {
  await publishAuroraAwhpAction(
    id,
    {
      boiler: {
        main_power: value
      }
    },
    config
  )
}

export async function setAuroraHeatingElement1RelayStatus(
  id: string,
  value: AuroraHeatingElementStatus,
  config?: AxiosRequestConfig
) {
  await publishAuroraAwhpAction(
    id,
    {
      heating_element_1: {
        relay: value
      }
    },
    config
  )
}

export async function setAuroraHeatingElement2RelayStatus(
  id: string,
  value: AuroraHeatingElementStatus,
  config?: AxiosRequestConfig
) {
  await publishAuroraAwhpAction(
    id,
    {
      heating_element_2: {
        relay: value
      }
    },
    config
  )
}

export async function setAuroraHeatingElement1RelayMode(
  id: string,
  mode: AuroraHeatingElementMode,
  config?: AxiosRequestConfig
) {
  await publishAuroraAwhpAction(
    id,
    {
      heating_element_1: {
        mode
      }
    },
    config
  )
}

export async function setAuroraHeatingElement2RelayMode(
  id: string,
  mode: AuroraHeatingElementMode,
  config?: AxiosRequestConfig
) {
  await publishAuroraAwhpAction(
    id,
    {
      heating_element_2: {
        mode
      }
    },
    config
  )
}

export async function setAuroraHeatingElement1OffsetTemperature(
  id: string,
  offsetTemp: number,
  config?: AxiosRequestConfig
) {
  await publishAuroraAwhpAction(
    id,
    {
      heating_element_1: {
        offset_temp: offsetTemp
      }
    },
    config
  )
}

export async function setAuroraHeatingElement2OffsetTemperature(
  id: string,
  offsetTemp: number,
  config?: AxiosRequestConfig
) {
  await publishAuroraAwhpAction(
    id,
    {
      heating_element_2: {
        offset_temp: offsetTemp
      }
    },
    config
  )
}

export async function getAuroraAwhpActiveEvents(id: string, config?: AxiosRequestConfig) {
  const response = await api.get<AuroraAwhpEvent[]>(`/auroras/${id}/awhp/active-events`, config)

  return response.data
}

export async function getAuroraAwhpResponse(
  id: string,
  correlationId: string,
  config?: AxiosRequestConfig
) {
  const response = await api.get<AuroraAwhpResponse>(
    `/auroras/${id}/awhp/response/${correlationId}`,
    {
      ...config
    }
  )

  return response.data
}

export async function pollAuroraAwhpResponse(
  id: string,
  correlationId: string,
  config?: AxiosRequestConfig
) {
  const startTime = Date.now()

  const THREE_SECONDS = 3 * 1000
  const THIRTY_SECONDS = 30 * 1000

  while (Date.now() - startTime < THIRTY_SECONDS) {
    try {
      const status = await getAuroraAwhpResponse(id, correlationId, config)

      return status
    } catch {
      await wait(THREE_SECONDS)
    }
  }

  throw new Error('Timeout while polling for Aurora AWHP status')
}

async function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
